import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { IcoEmail16, IcoFaceid16, IcoSmartphone16 } from '@onefootprint/icons';
import type { ChallengeKind, IdentifiedUser } from '@onefootprint/request-types';
import { Button, PageShell, RadioSelect } from '@onefootprint/ui';
import type { FormEvent, JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayEmail, getDisplayPhone } from '@/idv2/features/identify/get-display-contact-info';
export type ChallengeKindType = 'sms' | 'email' | 'biometric';
const CHALLENGE_PRIORITY: Record<ChallengeKindType, number> = {
  biometric: 0,
  sms: 1,
  email: 2
};
const CHALLENGE_ICONS: Record<ChallengeKindType, Icon> = {
  sms: IcoSmartphone16,
  email: IcoEmail16,
  biometric: IcoFaceid16
};
export type LoginChallengeSelectProps = {
  loginRequirement: {
    user: IdentifiedUser;
  };
  methods: ChallengeKindType[];
  onSelect: (challengeKind: ChallengeKind) => void;
  title: string;
  subtitle: string;
  continueLabel: string;
  isLoading?: boolean;
};
const getChallengeTitle = (kind: ChallengeKindType, user: IdentifiedUser | undefined, t: ReturnType<typeof useTranslation<'idv2'>>['t']): string | JSX.Element => {
  const sendCodeTo = t($ => $.identify.login.sendCodeTo);
  if (kind === 'sms') {
    const displayPhone = getDisplayPhone({
      user
    });
    return displayPhone ? <>
        {sendCodeTo} <span data-dd-privacy="mask">{displayPhone}</span>
      </> : t($ => $.identify.login.sendViaSms);
  }
  if (kind === 'email') {
    const displayEmail = getDisplayEmail({
      user
    });
    return displayEmail ? <>
        {sendCodeTo} <span data-dd-privacy="mask">{displayEmail}</span>
      </> : t($ => $.identify.login.sendViaEmail);
  }
  return t($ => $.identify.login.passkey);
};
export const LoginChallengeSelect = t0 => {
  const $ = _c(29);
  const {
    loginRequirement,
    methods,
    onSelect,
    title,
    subtitle,
    continueLabel,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("idv2");
  let t2;
  if ($[0] !== methods) {
    t2 = [...methods].sort(_temp);
    $[0] = methods;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sortedMethods = t2;
  let t3;
  if ($[2] !== sortedMethods[0]) {
    t3 = () => sortedMethods[0] ?? "sms";
    $[2] = sortedMethods[0];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [selected, setSelected] = useState(t3);
  let t4;
  if ($[4] !== loginRequirement.user || $[5] !== sortedMethods || $[6] !== t) {
    let t5;
    if ($[8] !== loginRequirement.user || $[9] !== t) {
      t5 = kind => ({
        value: kind,
        title: getChallengeTitle(kind, loginRequirement.user, t),
        IconComponent: CHALLENGE_ICONS[kind]
      });
      $[8] = loginRequirement.user;
      $[9] = t;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = sortedMethods.map(t5);
    $[4] = loginRequirement.user;
    $[5] = sortedMethods;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const options = t4;
  let t5;
  if ($[11] !== onSelect || $[12] !== selected) {
    t5 = ev => {
      ev.preventDefault();
      onSelect(selected);
    };
    $[11] = onSelect;
    $[12] = selected;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSubmit = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => setSelected(value as ChallengeKindType);
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== options || $[16] !== selected) {
    t7 = <RadioSelect value={selected} onChange={t6} options={options} size="compact" />;
    $[15] = options;
    $[16] = selected;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== continueLabel || $[19] !== isLoading) {
    t8 = <Button type="submit" variant="primary" fullWidth={true} size="large" loading={isLoading} disabled={isLoading} data-dd-action-name="challenge:continue">{continueLabel}</Button>;
    $[18] = continueLabel;
    $[19] = isLoading;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== handleSubmit || $[22] !== t7 || $[23] !== t8) {
    t9 = <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full">{t7}{t8}</form>;
    $[21] = handleSubmit;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== subtitle || $[26] !== t9 || $[27] !== title) {
    t10 = <PageShell title={title} subtitle={subtitle}>{t9}</PageShell>;
    $[25] = subtitle;
    $[26] = t9;
    $[27] = title;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
};
function _temp(a, b) {
  return CHALLENGE_PRIORITY[a] - CHALLENGE_PRIORITY[b];
}